import { createStore } from 'zustand/vanilla';
import { INTRO } from 'Router/routes';
import { logger } from 'utils/logger';
import { newExternalUrl } from 'utils/urlHelpers';
import {
  adapters,
  restoreAfterExternalRedirect,
  SessionStatus,
  storeLegacyToken,
} from './adapters';
import { AuthMode, AuthModeOptions, resolveAuthMode } from './authMode';

export type SessionState = {
  /** `null` while the Auth mode is still resolving */
  mode: AuthMode | null;
  /** `null` until someone asks for the Session status */
  status: SessionStatus | null;
};

export type LegacySignIn = {
  username: string;
  token: string;
};

type SessionOptions = AuthModeOptions & {
  redirect: (url: string, how: 'assign' | 'replace') => void;
};

const defaultOptions: SessionOptions = {
  retryDelaysMs: [500, 1500],
  attemptTimeoutMs: 5000,
  redirect: (url, how) =>
    how === 'assign'
      ? window.location.assign(url)
      : window.location.replace(url),
};

const BANK_ID_SESSION_KEY = 'bankIdSessionId';

export function createSession(options: Partial<SessionOptions> = {}) {
  const { redirect, ...authModeOptions } = { ...defaultOptions, ...options };
  const logoutListeners = new Set<() => void>();
  const store = createStore<SessionState>()(() => ({
    mode: null,
    status: null,
  }));
  let modePromise: Promise<AuthMode> | null = null;
  let statusPromise: Promise<SessionStatus> | null = null;

  /** Resolves the Auth mode once per page load; later calls share the result */
  const resolveMode = () => {
    modePromise ??= resolveAuthMode(authModeOptions).then((mode) => {
      store.setState({ mode });
      return mode;
    });

    return modePromise;
  };

  const adapter = async () => adapters[await resolveMode()];

  const setStatus = (status: SessionStatus) => {
    store.setState({ status });
    return status;
  };

  /** Finds out the Session status once per page load; later calls share the result */
  const resolveStatus = () => {
    statusPromise ??= adapter()
      .then(({ checkStatus }) => checkStatus())
      .then(setStatus);

    return statusPromise;
  };

  /**
   * Forgets the session in this tab; the client leaves the page right after.
   * Storage goes first, so listeners can write fresh values that survive.
   */
  const clear = () => {
    sessionStorage.clear();
    // The Bank iD session belongs to this client; the next client on this browser must not see it
    localStorage.removeItem(BANK_ID_SESSION_KEY);
    statusPromise = Promise.resolve(setStatus('anonymous'));
    logoutListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        logger.error('[session] logout listener failed', { error });
      }
    });
  };

  return {
    store,
    resolveMode,
    resolveStatus,

    /** The client is back from the sign-in page: check that a session really exists */
    async confirmLogin() {
      statusPromise = null;
      const status = await resolveStatus();

      if (status === 'anonymous') {
        logger.error('[session] no session after returning from sign-in', {
          mode: store.getState().mode,
        });
      }

      return status;
    },

    /** Stores the token from a Legacy sign-in and returns it encoded */
    signInLegacy({ username, token }: LegacySignIn) {
      const authorizationToken = storeLegacyToken(username, token);
      statusPromise = Promise.resolve(setStatus('authenticated'));
      return authorizationToken;
    },

    /** Base URL, headers and credentials for a backend request */
    async requestAuth() {
      return (await adapter()).requestAuth();
    },

    /** Call before sending the client to another site that will send them back */
    async beforeExternalRedirect() {
      (await adapter()).saveForExternalRedirect();
    },

    /** Call once at startup, before anything asks for the Session status */
    restoreAfterExternalRedirect,

    /** Runs `listener` whenever the session ends; returns a function that removes it */
    onLogout(listener: () => void) {
      logoutListeners.add(listener);
      return () => {
        logoutListeners.delete(listener);
      };
    },

    /** Ends the session and leaves the registration */
    async logout({ redirectTo = newExternalUrl(INTRO) } = {}) {
      const { endSession } = await adapter();
      const url = await endSession(redirectTo);

      clear();
      logger.info('[session] logged out', { redirect: url });
      redirect(url, 'assign');
    },

    /** The backend rejected the session: clear it and start again at the intro */
    async expire() {
      clear();
      redirect(INTRO, 'replace');
    },

    /** Sends the client to the dashboard, still signed in; forgets the session here */
    async handOverToDashboard() {
      try {
        const { dashboardUrl } = await adapter();
        const url = await dashboardUrl();

        clear();
        redirect(url, 'assign');
      } catch (error) {
        logger.error('[session] hand-over to the dashboard failed', { error });
      }
    },
  };
}

export type Session = ReturnType<typeof createSession>;

export const session = createSession();
